import { useState, useEffect, useRef, useCallback } from "react";
import {
  getAmbulancesEnRoute,
  HUNTINGTON_HOSPITAL_ID,
} from "../services/firebase";
import { Ambulance } from "../types/ambulance";

const formatEtaForDisplay = (eta: number | null, timestamp?: Date): string => {
  if (!eta) return "Unknown";

  // If a timestamp is provided, calculate remaining time by subtracting elapsed time
  if (timestamp) {
    const now = new Date().getTime();
    const timestampMs = timestamp.getTime();
    const elapsedSeconds = (now - timestampMs) / 1000;
    
    // Calculate remaining seconds (don't go below 0)
    const remainingSeconds = Math.max(0, eta - elapsedSeconds);
    
    // Check if ETA has passed more than 4 minutes ago but status hasn't updated
    const minutesPastEta = (elapsedSeconds - eta) / 60;
    if (remainingSeconds === 0 && minutesPastEta >= 4) {
      return "Arrived *Estimated*";
    }
    
    const minutes = Math.ceil(remainingSeconds / 60);
    
    // Return "Imminent" when remaining time is 0
    if (minutes === 0) {
      return "Imminent";
    }
    
    if (minutes < 60) {
      return `${minutes} min${minutes !== 1 ? "s" : ""}`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours} hr${hours !== 1 ? "s" : ""} ${remainingMinutes} min${
        remainingMinutes !== 1 ? "s" : ""
      }`;
    }
  }

  // Default behavior (static ETA) if no timestamp provided
  const minutes = Math.ceil(eta / 60);

  if (minutes < 60) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hr${hours !== 1 ? "s" : ""} ${remainingMinutes} min${
      remainingMinutes !== 1 ? "s" : ""
    }`;
  }
};

// Simple notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 note
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Start with 0 volume and ramp up
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.1);

    // Play sound
    oscillator.start(audioContext.currentTime);

    // Fade out and stop
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
    console.error("Failed to play notification sound:", error);
  }
};

// Manage newly added ambulances with expiration times
interface NewAmbulanceEntry {
  unitId: string;
  expiresAt: number;
}

export const useAmbulances = () => {
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState<boolean>(false);

  // Track new ambulances with expiration time
  const newAmbulancesRef = useRef<NewAmbulanceEntry[]>([]);

  // Keep track of previously seen ambulance IDs
  const prevAmbulanceIdsRef = useRef<Set<string>>(new Set());

  // Flag to avoid playing sound on initial load
  const initialLoadCompletedRef = useRef<boolean>(false);

  // Time a unit remains marked as "new" (15 seconds)
  const NEW_AMBULANCE_DURATION = 15000;

  // Function to update the new ambulances reference and mark ambulances
  const updateNewAmbulanceStatus = useCallback(
    (ambulanceData: Ambulance[]): Ambulance[] => {
      const now = Date.now();

      // Clean expired entries
      newAmbulancesRef.current = newAmbulancesRef.current.filter(
        (entry) => entry.expiresAt > now
      );

      // Get current set of "new" unit IDs
      const newUnitIds = new Set(
        newAmbulancesRef.current.map((entry) => entry.unitId)
      );

      // Mark ambulances as new if they are in our tracking list
      return ambulanceData.map((amb) => ({
        ...amb,
        isNew: newUnitIds.has(amb.unitId),
      }));
    },
    []
  );

  // Debug function to log ambulance state
  const logAmbulanceState = useCallback(() => {}, [ambulances]);

  // Clean up expired "new" ambulances periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      // Clean up expired "new" ambulances
      const updatedNewList = newAmbulancesRef.current.filter(
        (entry) => entry.expiresAt > now
      );

      // Only update state if the list has changed
      if (updatedNewList.length !== newAmbulancesRef.current.length) {
        newAmbulancesRef.current = updatedNewList;
      }

      // Also clean up arrived ambulances that have been displayed for 15 minutes
      setAmbulances((current) => {
        const ARRIVAL_DISPLAY_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
        const PAST_ETA_REMOVAL_THRESHOLD = 12 * 60; // 12 minutes in seconds

        // Remove ambulances that have been arrived for more than 15 minutes
        // OR non-arrived ambulances that are 12+ minutes past their ETA
        // OR any ambulance with a last updated timestamp older than 1 hour
        const filtered = current.filter((amb) => {
          try {
            // Case 1: Remove officially arrived ambulances after 15 minutes
            if (amb.arrived && amb.arrivalTime) {
              const elapsed = now - amb.arrivalTime.getTime();
              const shouldKeep = elapsed < ARRIVAL_DISPLAY_DURATION;
              if (!shouldKeep) {
                console.log(`Removing arrived ambulance ${amb.unitId} after display duration`);
              }
              return shouldKeep;
            }
            
            // Case 2: Remove ambulances that are 12+ minutes past their ETA
            // Make sure we have all the data we need and the ambulance hasn't already arrived
            if (!amb.arrived && !amb.possiblyArrived && 
                amb.eta !== null && amb.eta !== undefined && 
                amb.timestamp && typeof amb.timestamp.getTime === 'function') {
              
              try {
                // Calculate when the ambulance was expected to arrive
                const etaMs = amb.eta * 1000; // Convert ETA to milliseconds
                const expectedArrivalTime = amb.timestamp.getTime() + etaMs;
                
                // Calculate how many minutes have passed since expected arrival
                const minutesPastEta = (now - expectedArrivalTime) / (60 * 1000);
                
                // Remove if more than 12 minutes past ETA
                if (minutesPastEta >= 12) {
                  console.log(`Removing ambulance ${amb.unitId} that is ${minutesPastEta.toFixed(1)} minutes past ETA`);
                  return false;
                }
              } catch (err) {
                console.error("Error calculating minutes past ETA:", err);
              }
            }
            
            // Case 3: Remove any ambulance with a last updated timestamp older than 1 hour
            if (amb.timestamp && typeof amb.timestamp.getTime === 'function') {
              const ONE_HOUR_MS = 60 * 60 * 1000; // 1 hour in milliseconds
              const lastUpdatedElapsed = now - amb.timestamp.getTime();
              
              if (lastUpdatedElapsed > ONE_HOUR_MS) {
                console.log(`Removing ambulance ${amb.unitId} with outdated timestamp (${Math.floor(lastUpdatedElapsed / (60 * 1000))} minutes old)`);
                return false;
              }
            }
            
            // Keep all other ambulances
            return true;
          } catch (error) {
            console.error("Error in ambulance cleanup filter:", error);
            return true; // Keep the ambulance if there's an error
          }
        });

        // Also update isNew flags based on the new list
        const updated = filtered.map((amb) => ({
          ...amb,
          isNew: updatedNewList.some((entry) => entry.unitId === amb.unitId),
        }));

        // Only return a new array if something changed
        if (updated.length !== current.length) {
          return updated;
        }
        return current;
      });
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Set up Firebase listener only once on initial mount
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      // Set up real-time listener for ambulances en route to Huntington hospital
      const unsubscribe = getAmbulancesEnRoute(
        HUNTINGTON_HOSPITAL_ID,
        (data) => {
          try {
            console.log(`[Debug] Raw ambulances data received:`, data.map(a => ({unitId: a.unitId, timestamp: a.timestamp})));
            // If we have no data, clear the ambulances list but don't trigger other effects
            if (data.length === 0) {
              setAmbulances([]);
              setLoading(false);
              return;
            }

            // Identify newly added ambulances
            const currentIds = new Set(data.map((amb) => amb.unitId));
            const prevIds = prevAmbulanceIdsRef.current;

            // Find ambulances that weren't in the previous set
            const justAddedIds: string[] = [];

            // On first load, mark ALL ambulances as "new" for better visibility
            if (!initialLoadCompletedRef.current && data.length > 0) {
              // First load - mark all ambulances as new
              data.forEach((amb) => justAddedIds.push(amb.unitId));
            } else {
              // Subsequent updates - only mark truly new ambulances
              currentIds.forEach((id) => {
                if (!prevIds.has(id)) {
                  justAddedIds.push(id);
                }
              });
            }

            // Update the list of previously seen ambulances
            prevAmbulanceIdsRef.current = currentIds;

            // Process newly added ambulances
            if (justAddedIds.length > 0) {
              const now = Date.now();

              // Play notification sound when new ambulances arrive (but not on initial load)
              if (audioEnabled && initialLoadCompletedRef.current) {
                playNotificationSound();
              }

              // Add new ambulances to the tracking list with expiration time
              newAmbulancesRef.current = [
                ...newAmbulancesRef.current,
                ...justAddedIds.map((id) => ({
                  unitId: id,
                  expiresAt: now + NEW_AMBULANCE_DURATION,
                })),
              ];
            }

            // Mark the initial load as completed
            if (!initialLoadCompletedRef.current) {
              initialLoadCompletedRef.current = true;
            }

            // Apply new status to ambulances - making sure to include all ambulances
            let dataWithNewFlag: Ambulance[];
            if (data.length > 0) {
              // Get all the currently tracked "new" ambulance IDs
              const newUnitIds = new Set(
                newAmbulancesRef.current.map((entry) => entry.unitId)
              );

              // Mark ambulances as new if they're in our tracking list
              // or if they already have the isNew flag (preserved from Firebase)
              dataWithNewFlag = data.map((amb) => {
                // An ambulance is new if it's in our tracking list OR
                // if it was already marked as new in the data from Firebase
                const isNewAmb =
                  newUnitIds.has(amb.unitId) || amb.isNew === true;

                return {
                  ...amb,
                  isNew: isNewAmb,
                };
              });

              // If we have ambulances that need to be marked as new,
              // make sure they're in our tracking list
              data.forEach((amb) => {
                if (amb.isNew && !newUnitIds.has(amb.unitId)) {
                  // Add to our new ambulances tracking with appropriate expiration
                  const now = Date.now();
                  newAmbulancesRef.current.push({
                    unitId: amb.unitId,
                    expiresAt: now + NEW_AMBULANCE_DURATION,
                  });
                }
              });
            } else {
              // No ambulances - just pass the data through
              dataWithNewFlag = data;
            }

            // Get current time for timestamp calculations
            const now = Date.now();
            
            console.log(`[Debug] Before final filtering. Ambulances:`, 
              dataWithNewFlag.map(a => ({
                unitId: a.unitId, 
                timestamp: a.timestamp, 
                minutesOld: a.timestamp ? Math.floor((now - a.timestamp.getTime()) / (60 * 1000)) : 'N/A'
              }))
            );

            // Apply one final filtering to ensure we don't display ambulances with outdated timestamps
            dataWithNewFlag = dataWithNewFlag.filter(amb => {
              if (!amb.timestamp || typeof amb.timestamp.getTime !== 'function') {
                console.log(`[Debug] Keeping ambulance ${amb.unitId} because timestamp is invalid`);
                return true;
              }
              
              const ONE_HOUR_MS = 60 * 60 * 1000;
              const lastUpdatedElapsed = now - amb.timestamp.getTime();
              
              if (lastUpdatedElapsed > ONE_HOUR_MS) {
                console.log(`[Debug] Filtering out ambulance ${amb.unitId} with outdated timestamp (${Math.floor(lastUpdatedElapsed / (60 * 1000))} minutes old)`);
                return false;
              }
              
              return true;
            });
            
            console.log(`[Debug] After final filtering. Ambulances:`, 
              dataWithNewFlag.map(a => ({unitId: a.unitId, timestamp: a.timestamp}))
            );

            // Ensure ambulances are properly sorted
            const sortedAmbulances = [...dataWithNewFlag].sort((a, b) => {
              // New ambulances come first
              if (a.isNew && !b.isNew) return -1;
              if (!a.isNew && b.isNew) return 1;

              // Then sort by ETA if available
              if (a.eta && b.eta) return a.eta - b.eta;
              if (a.eta && !b.eta) return -1;
              if (!a.eta && b.eta) return 1;

              return 0;
            });

            setAmbulances(sortedAmbulances);
            setLoading(false);

            // Debug log the current state
            setTimeout(() => logAmbulanceState(), 500);
          } catch (callbackError) {
            console.error("Error processing ambulance data:", callbackError);
            setError("Error processing ambulance data");
            setLoading(false);
          }
        }
      );

      // Clean up the listener when component unmounts
      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error("Failed to fetch ambulance data:", err);
      setError("Failed to fetch ambulance data");
      setLoading(false);
      return () => {};
    }
  }, []); // Empty dependency array ensures this runs only once

  // Handle audio toggle
  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => !prev);
  }, []);

  // Calculate summary stats
  const ambulanceCount = ambulances.length;

  // Find the next arriving ambulance (one with the lowest ETA that has location data)
  const nextAmbulance =
    ambulances.find((amb) => amb.hasLocation && amb.eta !== null) || null;
  const nextAmbulanceETA = nextAmbulance?.eta;

  // Calculate arrival time in New York time
  const calculateArrivalTime = (eta: number | null | undefined, ambulance?: Ambulance | null): string | null => {
    if (eta === undefined || eta === null) return null;
    
    // Use the ambulance's timestamp if available, otherwise use current time
    let baseTime: Date;
    if (ambulance && ambulance.timestamp) {
      baseTime = ambulance.timestamp;
    } else {
      baseTime = new Date();
    }
    
    // Calculate arrival time by adding ETA seconds to base time
    const arrivalTime = new Date(baseTime.getTime() + eta * 1000);
    
    // Format the time in New York timezone
    return arrivalTime.toLocaleTimeString([], { 
      hour: "2-digit", 
      minute: "2-digit",
      timeZone: "America/New_York"
    });
  };
  
  const nextAmbulanceArrivalTime = calculateArrivalTime(nextAmbulanceETA, nextAmbulance);

  // Check if all ambulances are estimated to have arrived (past the 4-minute cutoff)
  const allAmbulancesEstimatedArrived = ambulances.length > 0 && 
    ambulances.every(amb => {
      // Skip ambulances that have officially arrived
      if (amb.arrived || amb.possiblyArrived) return true;
      
      // For others, check if they're past the 4-minute cutoff
      if (amb.eta && amb.timestamp) {
        const now = new Date().getTime();
        const elapsedSeconds = (now - amb.timestamp.getTime()) / 1000;
        const minutesPastEta = (elapsedSeconds - amb.eta) / 60;
        return minutesPastEta >= 4;
      }
      return false;
    });

  // Format ETA in minutes if available
  let formattedNextETA: string;
  
  if (allAmbulancesEstimatedArrived) {
    formattedNextETA = "All Ambulances Arrived";
  } else if (nextAmbulance) {
    if (nextAmbulanceETA) {
      formattedNextETA = formatEtaForDisplay(nextAmbulanceETA, nextAmbulance.timestamp);
    } else {
      formattedNextETA = "Calculating..";
    }
  } else {
    formattedNextETA = "No ETA available";
  }

  // Count number of ambulances with valid locations
  const ambulancesWithLocation = ambulances.filter((a) => a.hasLocation).length;

  return {
    ambulances,
    loading,
    error,
    ambulanceCount,
    nextAmbulance,
    formattedNextETA,
    nextAmbulanceArrivalTime,
    ambulancesWithLocation,
    audioEnabled,
    toggleAudio,
  };
};

export default useAmbulances;
